import React, { useState } from 'react';
import { knowledgeArticles } from '../mock';
import { BookOpen, Calendar, ArrowRight, X, Check } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';

const Knowledge = () => {
  const [selectedArticle, setSelectedArticle] = useState(null);

  return (
    <section id="knowledge" className="py-24 bg-gradient-to-br from-[#1a1918] to-[#2d2826]">
      <div className="container mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="flex items-center justify-center space-x-3 mb-4">
            <BookOpen className="w-8 h-8 text-[#d4af37]" />
            <h2 className="text-4xl md:text-5xl font-bold text-white">
              Knowledge Hub
            </h2>
          </div>
          <p className="text-lg text-[#bbb5ae] max-w-2xl mx-auto">
            Insights, tips, and industry knowledge from our 13+ years of experience
          </p>
        </div>

        {/* Articles Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {knowledgeArticles.map((article, index) => (
            <Card
              key={article.id}
              onClick={() => setSelectedArticle(article)}
              className="overflow-hidden bg-white hover-lift group cursor-pointer"
              style={{
                animationDelay: `${index * 0.1}s`,
                opacity: 0,
                animation: 'fadeInUp 0.8s ease-out forwards'
              }}
            >
              <CardContent className="p-0">
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={article.image}
                    alt={article.title}
                    className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700"
                  />
                  <div className="absolute top-4 right-4 bg-[#d4af37] text-[#1a1918] px-3 py-1 rounded-full text-xs font-semibold">
                    NEW
                  </div>
                </div>
                <div className="p-6">
                  <div className="flex items-center space-x-2 text-[#736c64] text-sm mb-3">
                    <Calendar className="w-4 h-4" />
                    <span>{new Date(article.date).toLocaleDateString()}</span>
                  </div>
                  <h3 className="text-xl font-bold text-[#1a1918] mb-3 group-hover:text-[#d4af37] transition-colors">
                    {article.title}
                  </h3>
                  <p className="text-[#736c64] mb-4">{article.excerpt}</p>
                  <div className="flex items-center space-x-2 text-[#d4af37] font-semibold group-hover:space-x-4 transition-all">
                    <span>Read More</span>
                    <ArrowRight className="w-5 h-5" />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Article Modal */}
        {selectedArticle && (
          <Dialog open={!!selectedArticle} onOpenChange={() => setSelectedArticle(null)}>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="text-3xl font-bold text-[#1a1918] pr-8">
                  {selectedArticle.title}
                </DialogTitle>
              </DialogHeader>
              <div className="mt-4">
                <img
                  src={selectedArticle.image}
                  alt={selectedArticle.title}
                  className="w-full h-64 object-cover rounded-lg mb-6"
                />
                <div className="flex items-center space-x-2 text-[#736c64] text-sm mb-4">
                  <Calendar className="w-4 h-4" />
                  <span>{new Date(selectedArticle.date).toLocaleDateString()}</span>
                </div>
                <h4 className="text-xl font-semibold text-[#d4af37] mb-4">
                  {selectedArticle.subtitle}
                </h4>
                <p className="text-[#736c64] leading-relaxed mb-6 whitespace-pre-line">
                  {selectedArticle.content}
                </p>
                {selectedArticle.highlights && (
                  <div className="space-y-3 mb-6">
                    <h5 className="text-lg font-bold text-[#1a1918] mb-4">Key Highlights:</h5>
                    {selectedArticle.highlights.map((highlight, idx) => (
                      <div key={idx} className="flex items-start space-x-3">
                        <Check className="w-5 h-5 text-[#d4af37] mt-1 flex-shrink-0" />
                        <p className="text-[#736c64]">{highlight}</p>
                      </div>
                    ))}
                  </div>
                )}
                <div className="flex justify-center mt-8">
                  <a href="#contact">
                    <button className="bg-gradient-to-r from-[#d4af37] to-[#ffd700] text-[#1a1918] px-8 py-3 rounded-lg font-semibold hover:shadow-lg hover:shadow-[#d4af37]/50 transition-all duration-300 shine-effect">
                      Get Started Today
                    </button>
                  </a>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>
    </section>
  );
};

export default Knowledge;